import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useSelector, type ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import cn from 'classnames';
import * as actions from 'actions';
import { Card, Tabs } from '_atoms';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import { ExperienceTabContent, JobAssignedTabContent, PopupReassignCandidate } from '_molecules';
import { Timeline, CandidateDetails } from '_organisms';
import history from 'helpers/history';
import { MixpanelService } from 'helpers/Mixpanel';
import { clearCandidateData } from 'store/reducers/candidateReducer';
import { getCandidateData } from 'store/selectors/candidateSelectors';
import { TABS_OPTIONS } from './constants';
import './style.scss';

interface OwnProps {
  isSSC?: boolean;
  candidateLoading?: boolean;
  /** Passed by some routes; component reads id from useParams instead. */
  id?: string;
}

const mapDispatchToProps = (dispatch: Parameters<typeof bindActionCreators>[1]) => ({
  getCandidateInfo: bindActionCreators(actions.getCandidateInfo, dispatch),
  getReminders: bindActionCreators(actions.getReminders, dispatch),
  getJobs: bindActionCreators(actions.getJobs, dispatch),
  getRespRecruiters: bindActionCreators(actions.getResponsibleRecruiter, dispatch),
  clearCandidateData: bindActionCreators(clearCandidateData, dispatch),
  getTimeline: bindActionCreators(actions.getTimeline, dispatch),
});

const connector = connect(null, mapDispatchToProps);

type Props = ConnectedProps<typeof connector> & OwnProps;

export const CandidateProfile: React.FC<Props> = ({ isSSC, ...props }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(TABS_OPTIONS[0].key);
  const [assignPopup, setAssignPopup] = useState(false);

  const candidateData = useSelector(getCandidateData);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    props.getCandidateInfo(id, isSSC);
    if (isSSC) actions.markCandidateAsViewed(id);
    else props.getJobs(id);
    const mixpanel = new MixpanelService();
    mixpanel.track('View Page.Candidate Profile', {
      'Candidate ID': id,
    });

    window.scrollTo({ top: 0 });

    return () => {
      props.clearCandidateData();
    };
  }, [id, isSSC]);

  const showAssignPopup = useCallback(() => {
    setAssignPopup(true);
  }, []);

  const closeAssignPopup = () => {
    setAssignPopup(false);
  };

  const fullName =
    candidateData.first_name_latin && `${candidateData.first_name_latin} ${candidateData.last_name_latin}`;

  return (
    <>
      <div className="wrapper-content candidate-details-page">
        {!isSSC && (
          <BreadCrumbs
            element={t('translation:candidate_profile.breadcrumb_candidates')}
            elementName={fullName}
            path="/candidates"
          />
        )}
        <div className="candidate-details-page__main-info">
          <CandidateDetails showAssignPopup={showAssignPopup} candidateData={candidateData} isSSC={isSSC} />
          <Card className={cn('candidate-job-experience', { 'is-self-submitted': isSSC })}>
            {!isSSC && (
              <Tabs
                activeTab={tab}
                onTabClick={setTab}
                items={TABS_OPTIONS}
                className="candidate-job-experience__tabs"
              />
            )}
            <div className="candidate-job-experience__content">
              {tab === TABS_OPTIONS[0].key && <ExperienceTabContent candidateData={candidateData} />}
              {tab === TABS_OPTIONS[1].key && <JobAssignedTabContent />}
            </div>
          </Card>
        </div>
        {!isSSC && <Timeline candidateData={candidateData} />}
      </div>
      <PopupReassignCandidate
        candidate={candidateData}
        open={assignPopup}
        onAssign={() => {
          if (isSSC) {
            history.push(`/candidates/${candidateData.uuid}/show`);
          } else {
            props.getJobs(id);
            props.getCandidateInfo(id);
            props.getTimeline(id, undefined, true);
          }
          closeAssignPopup();
        }}
        onClose={closeAssignPopup}
      />
    </>
  );
};

export default connector(CandidateProfile);
